import { useEffect, useState } from 'react';
import { useCachedBlobUrl } from '../../Services/useBlobDownloader';

import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

const ImageGallery = ({ message, isEditable = false, onImagesChange }) => {
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLightboxOpen, setLightboxOpen] = useState(false);
  const { getCachedBlobUrl } = useCachedBlobUrl();

  const [visibleKeys, setVisibleKeys] = useState(
    Array.isArray(message?.images) ? message.images : [],
  );

  useEffect(() => {
    if (Array.isArray(message?.images)) {
      setVisibleKeys(message.images);
    } else {
      setVisibleKeys([]);
    }
  }, [message?.id, message?.images]);

  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    if (!Array.isArray(visibleKeys) || visibleKeys.length === 0) {
      setGalleryImages([]);
      return;
    }

    const ac = new AbortController();

    (async () => {
      try {
        const results = await Promise.all(
          visibleKeys.map(async (item, i) => {
            const key = item.key ?? item;
            const url = item.url ?? null;

            let blobUrl;
            if (!url) {
              blobUrl = await getCachedBlobUrl(key, 'image', {
                signal: ac.signal,
              });
            } else {
              blobUrl = url;
            }

            return {
              id: `${message?.id ?? 'msg'}-${i}`,
              src: blobUrl,
              alt: `Image ${i + 1}`,
              originalKey: key,
            };
          }),
        );
        setGalleryImages(results);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading images:', err);
        }
      }
    })();

    return () => ac.abort();
  }, [message?.id, visibleKeys, getCachedBlobUrl]);

  if (galleryImages.length === 0) {
    return null;
  }

  const backendFlag = message?.gallery;
  const isGrid =
    backendFlag === 'true' ? true : backendFlag === 'false' ? false : galleryImages.length > 1;

  const chunk = (arr, n) =>
    arr.reduce((rows, _, i) => (i % n ? rows : [...rows, arr.slice(i, i + n)]), []);

  const openLightbox = (idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  const handleDeleteClick = (idx, e) => {
    e.stopPropagation();
    if (!isEditable) return;

    setVisibleKeys((prevKeys) => {
      const nextKeys = [...prevKeys];
      nextKeys.splice(idx, 1);

      if (onImagesChange) {
        onImagesChange(message.id, nextKeys);
      }

      return nextKeys;
    });
  };

  const handleDownloadClick = (e, src, filename) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = src;
    link.download = filename || 'image.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="media-container">
      {!isGrid && (
        <div
          className={`single-image-container ${isEditable ? 'editable' : ''}`}
          onClick={() => openLightbox(0)}
          style={{ position: 'relative', overflow: 'hidden' }}
        >
          <img src={galleryImages[0].src} alt="media" className="single-image" />

          <div className="image-hover-overlay">
            <button
              type="button"
              className="download-image-btn"
              onClick={(e) => handleDownloadClick(e, galleryImages[0].src, 'image-1.jpg')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </button>
          </div>

          {isEditable && (
            <button
              type="button"
              className="remove-file-button image-delete-btn"
              onClick={(e) => handleDeleteClick(0, e)}
              title="Delete image"
              aria-label="Delete image"
              style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '12px',
                lineHeight: 1,
                padding: '4px 6px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          )}
        </div>
      )}

      {isGrid && (
        <div className="custom-gallery-container">
          {chunk(galleryImages, 3).map((row, rIdx) => (
            <div key={rIdx} className={`gallery-row gcol-${row.length}`}>
              {row.map((img, cIdx) => {
                const globalIdx = rIdx * 3 + cIdx;
                return (
                  <div
                    key={img.id}
                    className={`gallery-item ${isEditable ? 'editable' : ''}`}
                    onClick={() => openLightbox(globalIdx)}
                    style={{ position: 'relative', overflow: 'hidden' }}
                  >
                    <img src={img.src} alt="media" className="gallery-thumb" />

                    <div className="image-hover-overlay">
                      <button
                        type="button"
                        className="download-image-btn"
                        onClick={(e) =>
                          handleDownloadClick(e, img.src, `image-${globalIdx + 1}.jpg`)
                        }
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                      </button>
                    </div>

                    {isEditable && (
                      <button
                        type="button"
                        className="remove-file-button image-delete-btn"
                        onClick={(e) => handleDeleteClick(globalIdx, e)}
                        title="Delete image"
                        aria-label="Delete image"
                        style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          background: 'rgba(0,0,0,0.6)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '12px',
                          fontSize: '12px',
                          lineHeight: 1,
                          padding: '4px 6px',
                          cursor: 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <Lightbox
        open={isLightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={galleryImages}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        plugins={[Zoom]}
        zoom={{ maxZoomPixelRatio: 5 }}
        styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' } }}
        render={{
          slidePrevButton: galleryImages.length > 1 ? undefined : () => null,
          slideNextButton: galleryImages.length > 1 ? undefined : () => null,
        }}
        carousel={{
          finite: galleryImages.length === 1,
        }}
      />
    </div>
  );
};

export default ImageGallery;
